<script setup lang="ts">
  /**
   * Record row — 03-components/13c-record-row · OSCloud DS 1.1.170
   *
   * A read-only list row for a short register: saved cards, saved addresses, API keys, the
   * files picked for an upload. Two to five rows, no sorting, no pagination, no row-level
   * navigation, and one or two actions on the row.
   *
   * Not a selectable option (`BfSelectionCard` is a form control, and a `<label>` may not
   * contain a row action). Not a clickable row that navigates. Not a table: no header row, no
   * sorting, no density modifier.
   *
   * The row itself has no modifiers — media, sub-line, actions and control are optional by
   * omission, and `actions`, `control` and `meta` are mutually exclusive: a row is something
   * you act on, something you set, or something that states a fact, never two.
   */
  import BfIconCheck from '@/components/ui/icons/BfIconCheck.vue'

  withDefaults(
    defineProps<{
      /** What the row is. */
      label?: string
      /** One line under it: a size, an expiry, a qualifier. */
      sub?: string
      /**
       * A checklist step's state, shown as a 24px disc in the leading slot: filled with a check
       * when done, a hollow ring when not. Only done or todo — "failed" or "in progress" is a
       * badge in `meta`, never a tone on the disc. The disc is aria-hidden, so the state must
       * also be in text (the `meta` badge, or the sub-line and its action). Mutually exclusive
       * with `media`: the leading slot shows a mark or a state.
       */
      status?: 'done' | 'todo'
    }>(),
    { label: '', sub: '', status: undefined },
  )
</script>

<template>
  <li class="record-row">
    <span v-if="status" class="record-row__status" :data-state="status" aria-hidden="true"
      ><BfIconCheck :size="12"
    /></span>
    <span v-else-if="$slots.media" class="record-row__media"><slot name="media" /></span>

    <span class="record-row__text">
      <span class="record-row__label"
        ><slot name="label">{{ label }}</slot></span
      >
      <span v-if="sub || $slots.sub" class="record-row__sub"
        ><slot name="sub">{{ sub }}</slot></span
      >
    </span>

    <span v-if="$slots.actions" class="record-row__actions"><slot name="actions" /></span>
    <span v-else-if="$slots.control" class="record-row__control"><slot name="control" /></span>
    <span v-else-if="$slots.meta" class="record-row__meta"><slot name="meta" /></span>
  </li>
</template>

<style scoped lang="scss">
  /* Surface, padding and radius are the selection card's resting values, so a read-only register
     and a selectable set look like siblings. No hover: the row is not a target. */
  .record-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--bf-sp-2);
    padding: 14px 16px;
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    background: var(--bf-white);
  }

  /* A fixed 46px slot with NO fill: half the marks a register carries bring their own white
     card body, and a filled frame renders those as a box inside a box. The mark is sized by
     height with width free — a fixed square silently shrinks every mark wider than 1:1. */
  .record-row__media {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: var(--bf-r-md);
  }

  /* A BARE mark only — direct children. A mark that arrives in an .icon-frame is sized by
     the frame, and a descendant selector here would overrule it. */
  .record-row__media > :deep(img),
  .record-row__media > :deep(svg) {
    width: auto;
    max-width: 100%;
    height: 20px;
  }

  /* Same centre line as __media with a smaller footprint, so a checklist and a register stay
     aligned when they share a box. The state is data-state, never a modifier — it is runtime. */
  .record-row__status {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1.5px solid var(--bf-dusky-2);
    border-radius: var(--bf-r-pill);
    background: var(--bf-white);
    color: var(--bf-white);
  }

  .record-row__status > svg {
    opacity: 0;
  }

  .record-row__status[data-state='done'] {
    border-color: var(--bf-success);
    background: var(--bf-success);
  }

  .record-row__status[data-state='done'] > svg {
    opacity: 1;
  }

  .record-row__text {
    display: flex;
    flex: 1 1 12rem;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .record-row__label {
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-base);
    font-weight: var(--bf-fw-semibold);
    overflow-wrap: anywhere;
    color: var(--bf-fg-1);
  }

  /* A done step's text steps back so the open steps read first — read off the disc, so the row
     itself carries no class. */
  .record-row:has(> .record-row__status[data-state='done']) .record-row__label {
    color: var(--bf-fg-2);
  }

  .record-row__sub {
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs);
    overflow-wrap: anywhere;
    color: var(--bf-fg-2);
  }

  /* The three trailing slots share one geometry. No rule is needed for narrow widths: the row
     already wraps over a `flex: 1 1 12rem` text column, so a trailing slot drops to its own
     line on its own. */
  .record-row__actions,
  .record-row__control,
  .record-row__meta {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: var(--bf-sp-1);
  }

  /* The row never restyles what sits in `control`: the control's own component owns its metrics
     and its states. */
  .record-row__actions {
    position: relative;
  }

  /* ─── The row's own action ──────────────────────────────────────────
     A consumer's `<button class="record-row__action">`, so it is reached through :deep(). The
     recipe is the data table's row action, so an action reads the same whether the record sits
     in a table or in this list. */
  .record-row__actions :deep(.record-row__action) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: 0;
    border-radius: var(--bf-r-sm);
    background: none;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    font-weight: var(--bf-fw-semibold);
    text-decoration: none;
    cursor: pointer;
    color: var(--bf-orange-dark);
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  .record-row__actions :deep(.record-row__action:hover:not(:disabled)) {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .record-row__actions :deep(.record-row__action:focus-visible) {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: 2px;
  }

  .record-row__actions :deep(.record-row__action svg) {
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
  }

  .record-row__actions :deep(.record-row__action--destructive) {
    color: var(--bf-error-accent);
  }

  /* Icon-only: a 28px square, quiet until hover. Resting on secondary text, never tertiary —
     that is this control's disabled tone, and the two would read alike. */
  .record-row__actions :deep(.record-row__action--icon-only) {
    justify-content: center;
    gap: 0;
    width: 28px;
    height: 28px;
    border: 1px solid transparent;
    color: var(--bf-fg-2);
    transition:
      color var(--bf-dur-base) var(--bf-ease),
      border-color var(--bf-dur-base) var(--bf-ease);
  }

  .record-row__actions :deep(.record-row__action--icon-only:hover:not(:disabled)) {
    border-color: var(--bf-orange-dark);
    background: none;
    text-decoration: none;
    color: var(--bf-orange-dark);
  }

  .record-row__actions :deep(.record-row__action--icon-only:disabled) {
    border-color: transparent;
    background: none;
    color: var(--bf-fg-3);
  }

  .record-row__actions :deep(.record-row__action--icon-only svg) {
    width: 16px;
    height: 16px;
  }
</style>
